/**
 * 파일명 : pjt0103p.tsx
 * 설명 : 프로젝트 팀원 등록 팝업창
 * 수정내역
 ************************************************
 * Version	   수정일자			수정자	    수정내용      
 ************************************************
 * 1.0		2023-04-13			임종윤    최초작성
 **/

/************************************************
 * Import 영역  
 ************************************************/
/* React */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameMonth from 'dayjs/plugin/isSameOrAfter';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Pjt0103pAction } from '../../../redux/slice/pjt/pjt0103p_slice';
import { IComCdItemState, IExtUserItemState, IPjtMemberItemState, IUserItemState } from '../../../redux/models/pjt/pjt0103p_model';

/* AntD */
import { Layout, Row, Col, Space, Descriptions, DatePicker, Button, Typography, ConfigProvider, Select, Radio, Input } from 'antd';


/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';

const Pjt0103p = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const cmbList029  = useAppSelector((state) => state.Pjt0103p.cmbList029);
    const cmbList025  = useAppSelector((state) => state.Pjt0103p.cmbList025);
    const extUserList = useAppSelector((state) => state.Pjt0103p.extUserList);
    const userList    = useAppSelector((state) => state.Pjt0103p.userList);
    const pjtMember   = useAppSelector((state) => state.Pjt0103p.pjtMember);

    /** Render 변수 선언영역 **/
    /* AntD */
    const { Content } = Layout;
    const { RangePicker } = DatePicker;
    const { Text } = Typography;
    const { TextArea } = Input;


    /* 검색조건 프로젝트 */
    //소속 v
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     **/
    React.useEffect(() => {
        // 상세보기로 들어온 경우        
        if (props.pjtMember && Object.keys(props.pjtMember).length > 0) {
            dispatch(Pjt0103pAction.setPjtMember(props.pjtMember));
        } else {
            let todayDt = commonDate.maskDate(commonDate.getToday(), 'YYYY-MM-DD');

            const mmStDt  = new Date(todayDt);
            const mmEndDt = new Date(todayDt);

            const monMM = diffInMonths(mmEndDt, mmStDt);

            // 실제 총 투입 공수 계획
            dispatch(Pjt0103pAction.setPjtMember({ ...pjtMember, realManMon : monMM, plManMon : monMM }));
        }

        fnUser();
        fnCmb025();
        fnCmb029();
        extrUser();

        return () => {
            dispatch(Pjt0103pAction.initState());
        }
    }, []);

    /**
     * 기능 : diffInMonths
     * 설명 : 투입기간에 따른 투입 공수 값 구하기
     * param : 
     * return : 
     **/
    const diffInMonths = (end:Date, start:Date) => {

        dayjs.extend(customParseFormat);
        dayjs.extend(isSameMonth);


        let strStart = commonDate.formatDateToYYYYMMDD(start);
        let strEnd   = commonDate.formatDateToYYYYMMDD(end);
        let tmpStart = dayjs(strStart, 'YYYYMMDD');
        let tmpEnd   = dayjs(strEnd  , 'YYYYMMDD');
      
        let totalManMonths = 0;
        while (tmpStart.isSame(tmpEnd, 'month') || tmpStart.isBefore(tmpEnd)) {
            const startOfMonth = tmpStart.startOf('month');
            const endOfMonth = tmpStart.endOf('month');
        
            const from = tmpStart.isAfter(startOfMonth) ? tmpStart : startOfMonth;
            const to = tmpEnd.isBefore(endOfMonth) ? tmpEnd : endOfMonth;
        
            const daysWorked = to.diff(from, 'day') + 1;
            const daysInMonth = endOfMonth.diff(startOfMonth, 'day') + 1;
        
            totalManMonths += (daysWorked / daysInMonth);
        
            tmpStart = tmpStart.add(1, 'month').startOf('month');
          }
        
          return Math.floor((totalManMonths + Number.EPSILON) * 10) / 10;
    };

    /**
     * 기능 : 공통코드 조회 - 프로젝트등급코드
     * param : 
     */
    const fnCmb025 = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IComCdItemState[]) => {
                let arrOptions:IComCdItemState[] = [];

                datas.forEach((item:IComCdItemState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });
                dispatch(Pjt0103pAction.setCmbList025(arrOptions));
            }

        };
        common.getComCodeList('025', option);
    };
    /**
     * 기능 : 공통코드 조회 - 소속코드
     * param : 
     */
     const fnCmb029 = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IComCdItemState[]) => {
                let arrOptions:IComCdItemState[] = [];

                datas.forEach((item:IComCdItemState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });
                dispatch(Pjt0103pAction.setCmbList029(arrOptions));
            }

        };
        common.getComCodeList('029', option);
    };

    /**
     * 기능 : 외부직원 리스트 조회
     * param : 
     */
    const extrUser = () => {
        const arrOptions:IExtUserItemState[] = [];

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            endYn: 'N'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTR0005',
            success: (responseData:any) => {
                responseData.extUserList.sort((a:IExtUserItemState, b:IExtUserItemState) => (a.userNm > b.userNm) ? 1 : -1);
                responseData.extUserList.forEach((item:IExtUserItemState) => {
                    arrOptions.push({
                        label: item.userNm,
                        value: item.extEmpNo,
                        extEmpNo: item.extEmpNo,
                        pid: item.pid,
                        gndrTpCd: item.gndrTpCd,
                        belongDcd: item.belongDcd,
                        postCd: item.postCd,
                        postNm: item.postNm,
                    });
                });
                
                dispatch(Pjt0103pAction.setExtUserList(arrOptions));
            },
        });
    };
    

    /**
     * 기능 : fnUser
     * 설명 : 사용자 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
    const fnUser = () => {
        const arrOptions:IUserItemState[] = [];

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A'
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1);
                responseData.userList.forEach((item:IUserItemState) => {
                    arrOptions.push({
                        label: item.userNm,
                        value: item.empNo,
                        empNo: item.empNo,
                        pid: item.pid,
                        gndrTpCd: item.gndrTpCd,
                        postCd: item.postCd,
                        postNm: item.postNm,
                    });
                });
                
                dispatch(Pjt0103pAction.setUserList(arrOptions));
            },
        });
    };

    /**
     * 기능 : Number 입력값 변경 이벤트
     * 설명 : Decimal 타입 적합성 체크
     * param : 
     * return : None
     */
    const inputNumChange = React.useCallback((e:any) => {
        let tempIn = e.currentTarget.value.toString().replaceAll(',', '');

        if ((/^\d*\.?\d{0,2}$/.test(tempIn) || tempIn === '') && !isNaN(tempIn)) {
            let maxLen = e.currentTarget.maxLength;
            let difLen = maxLen > 20 ? 8 : 7;

            let splitVal = tempIn.toString().split('.');
            // 3자리마다 , 표시
            splitVal[0] = splitVal[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

            // . 포함일 경우
            if (splitVal[1] !== undefined) {
                let front = splitVal[0].replaceAll(',', '');

                // maxLen보다 큰경우 return
                if (front.length > maxLen - difLen) return false;
                if (front === '') e.currentTarget.value = 0.0 + Number(tempIn);

                // split해서 .이 없기 때문에 따로 추가해줌
                tempIn = splitVal[0] + '.' + splitVal[1];

                // . 가 없는 경우 소수점 및 , 제외한 길이까지 입력가능
            } else if (splitVal[1] === undefined && tempIn.length > maxLen - difLen) {
                return false;
            } else {
                tempIn = splitVal[0];
            }

            e.currentTarget.value = tempIn;
            const newItem = {...pjtMember, [e.currentTarget.name] : e.currentTarget.value};
            dispatch(Pjt0103pAction.setPjtMember(newItem));
        }
    }, [pjtMember]);
   

    /************************************************
     * 이벤트 함수 영역
     ************************************************/
    /**
     * 기능 : 등록/수정/삭제버튼 클릭 이벤트
     * 설명 : 등록/수정/삭제
     * param : 
     * return : 
     */
    const fn_Treat = (delYn:string) => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        let suserCtrAmt = pjtMember.userCtrAmt.toString().replaceAll(',', '');  // 계약단가
        let sextUserAmt = pjtMember.extUserAmt.toString().replaceAll(',', '');  // 외주인건비
        let plManMon = pjtMember.plManMon.toString().replaceAll(',', '')        // 계획공수
        let realManMon = pjtMember.realManMon.toString().replaceAll(',', '')    // 수행공수
        const requestData = {
            ...pjtMember,
            delYn: delYn,
            pjCd: props.pjCd,
            cusNm: props.cusNm,
            userCtrAmt: suserCtrAmt,
            extUserAmt: sextUserAmt,
            plManMon: plManMon,
            realManMon: realManMon,
            trDsc: props.trDsc,
            trDelCd: props.trDelCd,
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRPJTS0003',
            success: (_:any) => {
                const param = {
                    title : '위드정보 확인',
                    contents: '정상적으로 처리되었습니다.',
                    accept : () => {
                        if (delYn === 'Y' || props.trDsc === '01') {
                            // 팀원 삭제시 팝업닫기
                            props.cancel();
                        } 
                    }
                };

                common.openAlert(param);
            },
        });
    };

    /**
     * 기능 : 검색 입력값 변경 이벤트
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const inputOnChange = React.useCallback((e:any) => {
        const newItem = {...pjtMember, [e.currentTarget.name] : e.currentTarget.value};
        dispatch(Pjt0103pAction.setPjtMember(newItem));
    }, [pjtMember]);

    /**
     *  기능 : 실제 투입기간 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const realCalendar_onChange = (_:any, dateStrings:any) => {
        const startDate = dateStrings[0];
        const endDate   = dateStrings[1];
        const realStDt  = new Date(startDate);
        const realEndDt = new Date(endDate);
        const realMM    = diffInMonths(realEndDt, realStDt);

        dispatch(Pjt0103pAction.setPjtMember({ ...pjtMember, realManMon : realMM, realInDt : startDate, realOutDt : endDate }));
    };

    /**
     *  기능 : 계획 투입기간 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const plcalendar_onChange = (_:any, dateStrings:any) => {
        const startDate = dateStrings[0];
        const endDate   = dateStrings[1];
        const plStDt    = new Date(startDate);
        const plEndDt   = new Date(endDate);
        const plMM      = diffInMonths(plEndDt, plStDt);

        dispatch(Pjt0103pAction.setPjtMember({ ...pjtMember, plManMon : plMM, plInDt : startDate, plOutDt : endDate }));
    };

    /**
     *  기능 : 사용자 콤보 변경
     *  설명 : 사용자 콤보 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const userSelect = (inputValue:string) => {
        let inUser:IUserItemState = null;
        let exUser:IExtUserItemState = null;

        if (pjtMember.belongDcd === '20') {  // 외부 직원을 선택한 경우
            exUser = extUserList.filter(user => (user.extEmpNo === inputValue || user.pid === inputValue || user.gndrTpCd === inputValue) && user.belongDcd === '20')[0];

        } else if (pjtMember.belongDcd === '30') { // 업체 직원을 선택한 경우
            exUser = extUserList.filter(user => (user.extEmpNo === inputValue || user.pid === inputValue || user.gndrTpCd === inputValue) && user.belongDcd === '30')[0];

        } else { // 내부 직원을 선택한 경우
            inUser = userList.filter(user => (user.empNo === inputValue || user.pid === inputValue || user.gndrTpCd === inputValue))[0];
        }

        if (inUser) {
            const postCd = inUser.postCd;
            const pid = inUser.pid;
            const belongDcd = '10';
            const genderDcd = inUser.gndrTpCd;

            dispatch(Pjt0103pAction.setPjtMember({ ...pjtMember, memEmpNo : inputValue, postCd, pid, belongDcd, genderDcd }));
        } else {
            const postCd = exUser.postCd;
            const pid = exUser.pid;
            const belongDcd = exUser.belongDcd;
            const genderDcd = exUser.gndrTpCd;
            dispatch(Pjt0103pAction.setPjtMember({ ...pjtMember, memEmpNo : inputValue, postCd, pid, belongDcd, genderDcd }));
        }
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btnDelete': {
                const param = {
                    title : '위드정보 확인',
                    contents: '삭제 하시겠습니까?',
                    accept: () => {
                        // 삭제할 팀원 입력하지 않았을 경우 삭제되지 않도록
                        if (common.isEmptyString(pjtMember.memEmpNo)) {
                            const param = {
                                title : '위드정보 확인',
                                contents: '삭제할 팀원을 선택해주세요.',
                            };
                            common.openInfo(param);
                            return false;
                        }
                        fn_Treat('Y');
                    }
                };
                common.openConfirm(param);
                break;
            }
            case 'btnClose': {
                props.cancel();
                break;
            }

            case 'btnSave': {
                let colArr = Object.keys(pjtMember);
                let msg:string;
                for (let i = 0; i < colArr.length; i++) {
                    let colId = colArr[i];                              // pjtMember 컬럼Id
                    let obj:any = document.getElementById(colId);       // 컬럼Id가 name 으로된 태그
                    let chkVal;                                         // 체크할 태그의 value
                    let chkNm;                                          // pjtInfo 컬럼명
                    let flag = false;

                    if (!obj) obj = document.getElementsByName(colId)[0];  // 컬럼Id가 name 으로된 태그
                    if (!obj) continue;

                    // Input
                    if (!common.isEmptyString(obj.dataset.dirName)) {
                        chkVal = obj.value;
                        chkNm = obj.dataset.dirName;
                        flag = true;
                    }
                    else {
                        // Select
                        const selectElement = obj.closest(".ant-select");
                        if (selectElement) {
                            if (!common.isEmptyString(selectElement.dataset.dirName)) {
                                const key:string = colId;
                                chkVal = pjtMember[key as keyof IPjtMemberItemState];
                                chkNm  = selectElement.dataset.dirName;
                                flag   = true;
                            }
                        }
                    }
                    msg = chkNm + '을 입력하세요.';

                    // 입력값이 없을 경우 Alert 후 return
                    if (flag && (common.isEmptyString(chkVal))) {
                        const param = {
                            title : '위드정보 확인',
                            contents: msg,
                            accept: () => {
                                if (!common.isEmptyString(obj.dataset.dirName)) {
                                    setTimeout(() => {
                                        obj.focus();
                                    }, 300);
                                } else {
                                    const selectElement = obj.closest(".ant-select");
                                    if (selectElement) {
                                        const selector = selectElement.querySelector(".ant-select-selector") as HTMLElement;
                                        if (!common.isEmptyString(selectElement.dataset.dirName)) {
                                            setTimeout(() => {
                                                if (selector) selector.click();
                                            }, 300);
                                        }
                                    }
                                }
                            }
                        };
                        common.openInfo(param);
                        return false;
                    }
                }

                const empNoList = [...(props.pjtMemList || [])];
                const isDuplicate = empNoList.some((memEmpNo) => memEmpNo === pjtMember.memEmpNo);
                  
                if (isDuplicate && props.trDsc === '01') {
                    const param = {
                        title : '위드정보 확인',
                        contents: '이미 존재하는 팀원입니다.',
                    };
                    common.openInfo(param);
                    return;
                }

                const param = {
                    title : '위드정보 확인',
                    contents: props.trDsc !== '01' ? '저장 하시겠습니까?' : '등록 하시겠습니까?',
                    accept: () => {
                        fn_Treat('N');
                    }
                };
                common.openConfirm(param);
                break;
            }

            default:
        }
    };

    return (
        /* 화면 디자인 영역 */
        <Layout>
            <Content>
                <img className='title-icon' alt='' />
                <Text strong>프로젝트팀원등록</Text>
                <div>
                    <Descriptions bordered column={3} size='small'>
                        <Descriptions.Item label='* 소속' labelStyle={{ width: '8%' }}>
                            <Select 
                                id='belongDcd'
                                style={{
                                    width: '100%',
                                    minWidth: 200,
                                }}
                                data-dir-name='소속'
                                placeholder='소속을 선택하세요.'
                                optionFilterProp='label'
                                defaultValue=''
                                filterSort={(optionA: any, optionB: any) => {
                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                    return labelA.localeCompare(labelB);
                                }}
                                options={cmbList029}
                                value={pjtMember.belongDcd}
                                onChange={val => {
                                    if(props.trDsc !== '01') return false;
                                    dispatch(Pjt0103pAction.setPjtMember({...pjtMember, memEmpNo: null, pid : '', genderDcd : '', postCd: '',  belongDcd: val}));
                                }}
                            ></Select>
                        </Descriptions.Item>
                        <Descriptions.Item label='* 팀원'>
                            <Space>
                                {props.trDsc === '01' ?
                                <Select 
                                    id='memEmpNo'
                                    showSearch
                                    data-dir-name='팀원'
                                    placeholder='소속을 먼저 선택하세요.'
                                    style={{ width: 200, height: 30 }}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    optionFilterProp='label'
                                    defaultValue=''
                                    filterSort={(optionA: any, optionB: any) => {
                                        const labelA = (optionA?.label ?? '').toLowerCase();
                                        const labelB = (optionB?.label ?? '').toLowerCase();
                                        return labelA.localeCompare(labelB);
                                    }}
                                    options={pjtMember.belongDcd === '10' ? userList : !common.isEmptyString(pjtMember.belongDcd) ? extUserList.filter((item:IExtUserItemState) => { return item.belongDcd === pjtMember.belongDcd }) : null}
                                    value={pjtMember.memEmpNo}
                                    onChange={userSelect}
                                ></Select>
                                : pjtMember.memUserNm }
                                
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label='* 주민번호' >
                            {pjtMember.pid}
                        </Descriptions.Item>
                        <Descriptions.Item label='* 성별'>
                            <Radio.Group value={pjtMember.genderDcd}>
                            {userList.find(user => user.genderDcd === pjtMember.genderDcd)?.genderDcd}
                                <Radio value='01'>남</Radio>
                                <Radio value='02'>여</Radio>
                            </Radio.Group>
                        </Descriptions.Item>
                      
                        <Descriptions.Item label='* 직위'>
                            {userList.find(user => user.postCd === pjtMember.postCd)?.postNm}
                        </Descriptions.Item>
                        <Descriptions.Item label='* 역할(PM/컨설팅/분석/설계/개발)' labelStyle={{ width: '15%' }}>
                            <TextArea id='userRole' data-dir-name='역할' name='userRole' value={pjtMember.userRole} style={{ width: '80%' }} onChange={inputOnChange}/>
                        </Descriptions.Item>
                        <Descriptions.Item label='계획 등급코드'>
                            <Select 
                                style={{
                                    width: '100%',
                                    minWidth: 200,
                                }}
                                data-dir-name='계획 등급코드'
                                placeholder='계획 등급코드'
                                optionFilterProp='label'
                                defaultValue=''
                                filterSort={(optionA: any, optionB: any) => {
                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                    return labelA.localeCompare(labelB);
                                }}
                                options={cmbList025}
                                value={pjtMember.inGrdCd}
                                onChange={val => dispatch(Pjt0103pAction.setPjtMember({...pjtMember, inGrdCd: val}))}
                            ></Select>
                        </Descriptions.Item>
                        <Descriptions.Item label='계획 투입기간'>
                            <Space>
                                <ConfigProvider locale={locale}>
                                    <RangePicker
                                        style={{ width: 305 }}
                                        id='tx_calendar'
                                        allowClear={false}
                                        value={[dayjs(pjtMember.plInDt), dayjs(pjtMember.plOutDt)]}
                                        onChange={plcalendar_onChange}
                                    />
                                </ConfigProvider>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label='계획 총 투입 공수 계획'>
                            <Input id='inplManMon' data-dir-name='계획공수' name='plManMon' maxLength={15} value={pjtMember.plManMon.toLocaleString()} style={{ width: '80%', textAlign: 'right' }} onChange={inputNumChange} /><span style={{ marginLeft: '5px' }}>M/M</span>
                        </Descriptions.Item>
                        <Descriptions.Item label='실제 등급코드'>
                            <Select 
                                style={{
                                    width: '100%',
                                    minWidth: 200,
                                }}
                                data-dir-name='실제 등급코드'
                                placeholder='실제 등급코드'
                                optionFilterProp='label'
                                defaultValue=''
                                filterSort={(optionA: any, optionB: any) => {
                                    const labelA = (optionA?.label ?? '').toLowerCase();
                                    const labelB = (optionB?.label ?? '').toLowerCase();
                                    return labelA.localeCompare(labelB);
                                }}
                                options={cmbList025}
                                value={pjtMember.actlGrdCd}
                                onChange={val => dispatch(Pjt0103pAction.setPjtMember({...pjtMember, actlGrdCd: val}))}
                            ></Select>
                        </Descriptions.Item>
                        <Descriptions.Item label='실제  투입기간'>
                            <Space>
                                <ConfigProvider locale={locale}>
                                    <RangePicker
                                        style={{ width: 305 }}
                                        id='tx_calendar'
                                        allowClear={false}
                                        value={[dayjs(pjtMember.realInDt), dayjs(pjtMember.realOutDt)]}
                                        onChange={realCalendar_onChange}
                                    />
                                </ConfigProvider>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label='실제 총 투입 공수 계획'>
                            <Input id='inrealManMon' name='realManMon' maxLength={15} value={pjtMember.realManMon.toLocaleString()} style={{ width: '80%', textAlign: 'right' }} onChange={inputNumChange} /><span style={{ marginLeft: '5px' }}>M/M</span>
                        </Descriptions.Item>
                        <Descriptions.Item label='역할(대분류)'>
                            <Input id='inroleLclf' name='roleLclf' value={pjtMember.roleLclf} style={{ width: '100%' }} onChange={inputOnChange} />
                        </Descriptions.Item>
                        <Descriptions.Item label='역할(중분류)'>
                            <Input id='inroleMclf' name='roleMclf'  value={pjtMember.roleMclf} style={{ width: '90%' }} onChange={inputOnChange} />
                        </Descriptions.Item>
                        <Descriptions.Item label='역할(소분류)'>
                            <Input id='inroleSclf' name='roleSclf'  value={pjtMember.roleSclf} style={{ width: '80%' }} onChange={inputOnChange} />
                        </Descriptions.Item>
                        <Descriptions.Item label='역할(상세내용)'>
                            <Input id='inroleSclf' name='roleDtl'  value={pjtMember.roleDtl} style={{ width: '100%' }} onChange={inputOnChange} />
                        </Descriptions.Item>
                        <Descriptions.Item label='계약단가'>
                            <Input id='inuserCtrAmt' name='userCtrAmt' maxLength={23} value={pjtMember.userCtrAmt.toLocaleString()} style={{ width: '90%', textAlign: 'right' }} onChange={inputNumChange} /><span style={{ marginLeft: '5px' }}>원</span>
                        </Descriptions.Item>
                        <Descriptions.Item label='외주 인건비'>
                            <Input id='extUserAmt' name='extUserAmt' maxLength={23} value={pjtMember.extUserAmt.toLocaleString()} style={{ width: '80%', textAlign: 'right' }} onChange={inputNumChange} /><span style={{ marginLeft: '5px' }}>원</span>
                        </Descriptions.Item>
                    </Descriptions>


                </div>

                <Row justify='center' style={{ marginTop: 15 }}>
                    <Col>
                        <Space size='small'>
                            <Button
                                name='btnDelete'
                                className='background-color-primary-button'
                                onClick={(onClick)}
                                disabled={!common.isAdmin() && (common.getEmpNo() !== props.pmEmpNo || props.trDsc==='01')}

                            >
                                삭제
                            </Button>
                            <Button
                                name='btnSave'
                                className='background-color-primary-button'
                                onClick={onClick}
                                disabled={!common.isAdmin() && common.getEmpNo() !== props.pmEmpNo}
                            >
                                저장
                            </Button>
                            <Button
                                name='btnClose'
                                className='background-color-default-button'
                                onClick={() => props.accept()}
                            >
                                닫기
                            </Button>
                        </Space>
                    </Col>
                </Row>

            </Content>
        </Layout>
    );
}

export default Pjt0103p;
